// src/pages/Support/SuportePage.jsx

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Divider,
  Paper,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  HowToReg as HowToRegIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  ContactSupport as ContactSupportIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import Footer from '../../components/Footer/Footer';

const SuportePage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    tipo: '',
    mensagem: '',
  });

  // FAQs comuns baseadas no contexto do sistema
  const faqs = [
    {
      categoria: 'Pré-Inscrições',
      icon: <HowToRegIcon color="primary" />,
      questoes: [
        {
          pergunta: 'Como faço uma pré-inscrição?',
          resposta: 'Para fazer uma pré-inscrição, acesse a página de campanhas disponíveis, selecione a instituição e o curso desejado, e clique em "Pré-inscrever-se". Preencha todos os dados necessários e envie sua solicitação.'
        },
        {
          pergunta: 'Como acompanho o status da minha pré-inscrição?',
          resposta: 'Você pode acompanhar o status da sua pré-inscrição através do seu dashboard de estudante, na aba "Minhas Pré-inscrições".'
        }
      ]
    },
    {
      categoria: 'Campanhas',
      icon: <CampaignIcon color="primary" />,
      questoes: [
        {
          pergunta: 'Como encontro campanhas abertas?',
          resposta: 'As campanhas abertas estão disponíveis na página inicial do seu dashboard. Você pode filtrar por instituição ou curso específico.'
        },
        {
          pergunta: 'Qual o prazo para me inscrever em uma campanha?',
          resposta: 'Cada campanha tem seu próprio prazo, que está claramente indicado nos detalhes da campanha. Fique atento às datas limite!'
        }
      ]
    },
    {
      categoria: 'Instituições',
      icon: <SchoolIcon color="primary" />,
      questoes: [
        {
          pergunta: 'Como uma instituição pode se cadastrar no sistema?',
          resposta: 'Instituições interessadas devem entrar em contacto com nossa equipe administrativa através do formulário de contato ou pelo email institucional@zenkroll.co.mz'
        },
        {
          pergunta: 'Como gerenciar campanhas da minha instituição?',
          resposta: 'No dashboard institucional, você encontrará todas as ferramentas necessárias para criar, editar e gerir campanhas.'
        }
      ]
    },
    {
      categoria: 'Pagamentos',
      icon: <PaymentIcon color="primary" />,
      questoes: [
        {
          pergunta: 'Quais são as formas de pagamento aceitas?',
          resposta: 'Aceitamos pagamentos via transferência bancária, M-Pesa, E-Mola e outros métodos digitais. Consulte sua instituição para métodos específicos.'
        },
        {
          pergunta: 'Como emito o comprovante de pagamento?',
          resposta: 'Após a confirmação do pagamento, acesse seu dashboard e vá até a secção de pagamentos. Lá você encontrará a opção para gerar o comprovativo.'
        }
      ]
    }
  ];

  const handleSubmit = (event) => {
    event.preventDefault();
    // Implementar lógica de envio
    console.log('Formulário enviado:', formData);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h1" gutterBottom>
                Como podemos ajudar?
              </Typography>
              <Typography variant="h6" paragraph>
                Estamos aqui para apoiar sua jornada educacional
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom>
                  Canais de Atendimento
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<WhatsAppIcon />}
                      href="https://wa.me/seuNumero"
                      target="_blank"
                      sx={{ mb: 1 }}
                    >
                      WhatsApp
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      href="mailto:suporte@zenkroll.co.mz"
                    >
                      Email
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1 }}>
        {/* Seção de FAQ */}
        <Typography variant="h4" gutterBottom>
          Perguntas Frequentes
        </Typography>
        
        {faqs.map((categoria) => (
          <Box key={categoria.categoria} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {categoria.icon}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {categoria.categoria}
              </Typography>
            </Box>
            
            {categoria.questoes.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{faq.pergunta}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.resposta}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        {/* Formulário de Contato */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h4" gutterBottom>
            Entre em Contacto
          </Typography>
          <Paper sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Tipo de Suporte"
                    select
                    SelectProps={{ native: true }}
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  >
                    <option value="">Selecione o tipo de suporte</option>
                    <option value="pre-inscricao">Pré-Inscrição</option>
                    <option value="pagamento">Pagamento</option>
                    <option value="campanha">Campanhas</option>
                    <option value="tecnico">Suporte Técnico</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    label="Mensagem"
                    value={formData.mensagem}
                    onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<ContactSupportIcon />}
                  >
                    Enviar Mensagem
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        {/* Horário de Atendimento */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h4" gutterBottom>
            Horário de Atendimento
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Suporte Online
                  </Typography>
                  <Typography>
                    Segunda a Sexta: 08:00 - 18:00
                  </Typography>
                  <Typography>
                    Sábado: 09:00 - 13:00
                  </Typography>
                  <Chip
                    icon={<QuestionAnswerIcon />}
                    label="Tempo médio de resposta: 2 horas"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contato Institucional
                  </Typography>
                  <Typography>
                    Segunda a Sexta: 09:00 - 17:00
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Para assuntos institucionais, favor agendar horário.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default SuportePage;